import debounce from 'lodash/debounce';

import { createCurrentHeadingFinder, getHeadingQuerySelector, makeTocPathnameSet, parseHeadingLevel, shouldUseCustomAttributes } from '../../foreground/tableOfContents';
import { EpubToc, MobileTableOfContentsItem } from '../../types';
// eslint-disable-next-line import/no-cycle
import { getScrollingManager } from './getScrollingManager';
import { ScrollListenerEvent } from './ScrollingManager';
import type { MobileContentFrameWindow } from './types';

declare let window: MobileContentFrameWindow;

let headings: HTMLElement[] = [];

export default (isEpub = false, tocItems: EpubToc[] | undefined) => {
  const contentRoot: HTMLDivElement | null = document.querySelector('#document-text-content');
  if (!contentRoot) {
    throw new Error('No #document-text-content found');
  }

  const tocPathnameSet = makeTocPathnameSet(tocItems);
  const scrollingManager = getScrollingManager('tocInit');
  const isChunked = scrollingManager.isChunked;
  const useCustomAttributes = shouldUseCustomAttributes(contentRoot);

  const getCurrentHeading = createCurrentHeadingFinder({ isChunked, tocPathnameSet, isEpub, useCustomAttributes });

  const onScroll = debounce(async (event: ScrollListenerEvent) => {
    const currentFocusedElement = event.currentCenterElementInfo?.element;
    const currentChunkContainer = event.currentChunkElement;
    if (!currentFocusedElement || !headings.length) {
      return;
    }
    const currentHeadingId = getCurrentHeading(currentFocusedElement, currentChunkContainer);
    if (currentHeadingId) {
      window.portalGateToForeground.emit('toc_current_heading', { headingId: currentHeadingId });
    }
  }, 100);

  const scrollToActiveHeading = async (headingId: MobileTableOfContentsItem['id']) => {
    const scrollingManager = getScrollingManager('scrollToActiveHeading');
    scrollingManager.scrollToHeadingWithId(headingId, isEpub);
  };

  scrollingManager.addScrollListener(onScroll);

  window.portalGateToForeground.off('scroll-to-active-heading', scrollToActiveHeading);
  window.portalGateToForeground.on('scroll-to-active-heading', scrollToActiveHeading);

  const headingSelector = getHeadingQuerySelector(isEpub, useCustomAttributes);
  headings = Array.from(contentRoot.querySelectorAll(headingSelector));

  let highestLevelHeading = Infinity;
  // Give each heading an ID and track the highest level heading for indentation later
  headings.forEach((heading: HTMLElement, index: number): void => {
    if (!heading.id) {
      heading.id = `h__${index}`;
    }

    const level = parseHeadingLevel(heading, useCustomAttributes);
    // h1 is higher than h2
    if (level < highestLevelHeading) {
      highestLevelHeading = level;
    }
  });

  const tableOfContentsData: MobileTableOfContentsItem[] = headings.map(
    (heading: HTMLElement): MobileTableOfContentsItem => {
      const id = heading.id;
      const headingLevel = parseHeadingLevel(heading, useCustomAttributes) - highestLevelHeading + 1;
      const headingDisplay = heading.dataset?.rwTocTitle ?? heading.innerText;
      // Link to the ID using the document fragment identifier
      return { headingDisplay, headingLevel, id };
    },
  );

  window.portalGateToForeground.emit('table_of_contents', { tableOfContentsData });
};
